import { api } from '../services/axiosConfig';
import { FavoriteSystemsResponse, ShareScope, SystemActivityEntry } from '../types';

export interface SystemSearchResult {
  solar_system_id: number;
  name: string;
  system_class: string | null;
}

export const searchSystems = (q: string) =>
  api.get<SystemSearchResult[]>('/api/systems/search', { params: { q } }).then((r) => r.data);

export const fetchSystemActivity = (solarSystemId: number) =>
  api.get<SystemActivityEntry[]>(`/api/systems/${solarSystemId}/activity`).then((r) => r.data);

// Personal star (private to the account) + shared vault pin — see
// routes/systems.ts and routes/maps.ts respectively.
export const fetchFavoriteSystems = () => api.get<FavoriteSystemsResponse>('/api/systems/favorites').then((r) => r.data);

export const addFavoriteSystem = (solarSystemId: number) => api.post(`/api/systems/${solarSystemId}/favorite`);

export const removeFavoriteSystem = (solarSystemId: number) => api.delete(`/api/systems/${solarSystemId}/favorite`);

// ── Which Data Vaults hold this system, filtered to the ones this character
// can see (routes/systems.ts). Answers "have we already filed this?" from
// the system's own page, without opening every vault by hand. ──────────────

export interface SystemVaultEntry {
  vaultId: number;
  vaultName: string;
  entryId: number;
  label: string | null;
  tag: string | null;
  isHome: number;
  isPinned: number;
}

export const fetchSystemVaults = (solarSystemId: number) =>
  api.get<SystemVaultEntry[]>(`/api/systems/${solarSystemId}/vaults`).then((r) => r.data);

// ── General system information — corp-wide notes about a system,
// independent of any Data Vault (see routes/systems.ts, migration 011). ────

export interface SystemNote {
  id: number;
  ownerCharacterId: number;
  ownerName: string | null;
  body: string;
  shareScope: ShareScope;
  shareGroupId: number | null;
  updatedAt: string;
  isMine: boolean;
}

export const fetchSystemNotes = (solarSystemId: number) =>
  api.get<SystemNote[]>(`/api/systems/${solarSystemId}/notes`).then((r) => r.data);

export const saveSystemNote = (
  solarSystemId: number,
  body: { body: string; shareScope: ShareScope; shareGroupId?: number | null },
) => api.put(`/api/systems/${solarSystemId}/notes`, body);

export const deleteSystemNote = (solarSystemId: number) => api.delete(`/api/systems/${solarSystemId}/notes`);

// ── Fittings attached to a system — see routes/systems.ts, migration 011. ──

export interface SystemFittingLink {
  id: number;
  fittingId: number;
  name: string;
  shipName: string;
  /** False for a fit someone else shared with the corp/alliance/group. */
  isMine: boolean;
  ownerName: string | null;
  /** You own the fit, or you attached it here. Mirrors the DELETE rule. */
  canUnlink: boolean;
}

export const fetchSystemFittings = (solarSystemId: number) =>
  api.get<SystemFittingLink[]>(`/api/systems/${solarSystemId}/fittings`).then((r) => r.data);

export const attachSystemFitting = (solarSystemId: number, fittingId: number) =>
  api.post(`/api/systems/${solarSystemId}/fittings`, { fittingId });

export const detachSystemFitting = (solarSystemId: number, linkId: number) =>
  api.delete(`/api/systems/${solarSystemId}/fittings/${linkId}`);

// ── Intel search — find systems by what they ARE ──────────────────────────
//
// See routes/systems.ts: the corp's standing questions ("a C3 with a highsec
// static", "which Pulsar C5s do we know", "where can we mine Arkonor") were
// answered by leaving for anoik.is, because search could only match a name
// it already knew. This queries the whole curated catalogue, not just the
// systems someone has scanned.

export interface SystemFindFilters {
  class?: string;
  static?: string;
  effect?: string;
  resource?: string;
  limit?: number;
}

export interface SystemFindResult {
  solar_system_id: number;
  name: string;
  system_class: string;
  statics: { code: string; leadsTo: string }[];
  effect: string | null;
}

export interface SystemFindResponse {
  items: SystemFindResult[];
  /** Matches across the whole catalogue, not just this page — so the UI can
   * say "50 of 214" rather than implying the list is the whole field. */
  total: number;
  limit: number;
}

export const findSystems = (filters: SystemFindFilters) =>
  api.get<SystemFindResponse>('/api/systems/find', { params: filters }).then((r) => r.data);

export interface SystemFindOptions {
  classes: string[];
  statics: string[];
  effects: string[];
  resources: { value: string; resource_type: string }[];
}

/** Filter vocabulary, read from the data — a hardcoded list goes stale the
 * moment an import adds a variant, and a filter that matches nothing is
 * worse than no filter. */
export const fetchFindOptions = () =>
  api.get<SystemFindOptions>('/api/systems/find-options').then((r) => r.data);
